import styled from 'styled-components'
import { createRef, ReactElement, useEffect, useCallback } from 'react'
import { motion, useMotionValue, useTransform } from 'framer-motion'
import { isMobile } from 'react-device-detect'
import FadeIn from './FadeIn'

const RADIUS_BASIS = 1900
const WIDTH_BASIS = 502
const MARGIN_TOP_BASIS = 45

const SliderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 12%;
  width: 100%;
  position: relative;
  top: 88%;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.8) 100%);
`
const vtoScrubberRef = createRef<HTMLDivElement>()
const sliderContainerRef = createRef<HTMLDivElement>()

interface VtoScrubberProps {
  onDrag: ((event: MouseEvent | PointerEvent | TouchEvent, info: any) => void) | undefined
  onDragStart: ((event: MouseEvent | PointerEvent | TouchEvent, info: any) => void) | undefined
  onDragEnd: ((event: MouseEvent | PointerEvent | TouchEvent, info: any) => void) | undefined
  scale: number
  x: any
  slideFull: string
}

interface HandleProps {
  scale: number
  radius: number
  x: any
}

const VtoScrubber = ({ onDrag, onDragStart, onDragEnd, scale, x, slideFull }: VtoScrubberProps): ReactElement => {
  const y = useMotionValue(0)
  let height = isMobile ? '14%' : '100%'
  if (slideFull.toString() === 'true') height = '100%';
  if (slideFull.toString() === 'false') height = '14%';

  return (
    <motion.div
      className="topology-vtoScrubber"
      ref={vtoScrubberRef}
      dragConstraints={{
        top: 0,
        left: WIDTH_BASIS / 2.0 * scale * -1 * 0.88,
        right: WIDTH_BASIS / 2.0 * scale * 0.88,
        bottom: 0
      }}
      dragTransition={{ bounceStiffness: 3000, bounceDamping: 500 }}
      drag='x'
      dragElastic={0}
      dragMomentum={false}
      style={{
        x,
        y,
        height
      }}
      whileTap={{ cursor: 'grabbing' }}
      onDrag={onDrag}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
    </motion.div>
  )
}

const Handle = ({ scale, radius, x }: HandleProps): ReactElement => {
  const y = useTransform(x, (value) => {
    const dx = Math.abs(x.get())
    const dy = Math.sqrt(radius * radius - dx * dx)
    return Math.ceil((radius - dy) * -1)
  })

  return (
    <motion.div
      className="topology-sliderMotion"
      style={{
        x,
        y,
        scale,
        originX: 0.5,
        originY: 0,
        originZ: 0,
        marginTop: `${(MARGIN_TOP_BASIS * scale)}px`
      }}
    >
      <div className="topology-sliderHandle"></div>
    </motion.div>
  )
}
interface ArrowsSVGProps {
  scale: number
}

function Arrows({ scale }: ArrowsSVGProps): ReactElement {
  return (
    <motion.svg
      preserveAspectRatio='xMidYMid meet'
      width='502'
      height='24'
      viewBox='0 0 502 24'
      fill='none'
      style={{
        scale
      }}
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        className="topology-sliderTrack"
        d='M501.608 6.20619C502.047 6.54206 502.13 7.16975 501.794 7.60816L496.321 14.7526C495.985 15.191 495.357 15.2742 494.919 14.9383C494.48 14.6024 494.397 13.9747 494.733 13.5363L499.598 7.18571L493.247 2.32056C492.809 1.98469 492.726 1.357 493.062 0.918586C493.398 0.480167 494.025 0.397033 494.464 0.732902L501.608 6.20619ZM0.206177 7.60814C-0.129699 7.16972 -0.0465691 6.54204 0.391877 6.20617L7.53632 0.732881C7.97473 0.397012 8.60242 0.480146 8.93826 0.918565C9.27414 1.35698 9.19101 1.98467 8.75259 2.32054L2.40198 7.18568L7.26712 13.5363C7.603 13.9747 7.51987 14.6024 7.08145 14.9383C6.64304 15.2741 6.01535 15.191 5.67947 14.7526L0.206177 7.60814ZM251 22C338.57 22 422.658 16.3673 500.869 6.00868L501.131 7.99136C422.83 18.362 338.654 24 251 24L251 22ZM1.13132 6.00866C79.3425 16.3673 163.43 22 251 22L251 24C163.346 24 79.17 18.3619 0.868714 7.99134L1.13132 6.00866Z'
        fill='white'
      />
    </motion.svg>
  )
}

interface SVGSliderProps {
  usableWidth: number
  className: string
  onChange: (percentage: number) => void
  slideFull: string
}

function SVGSlider({ usableWidth, onChange, slideFull }: SVGSliderProps): ReactElement {
  const x = useMotionValue(0)

  useEffect(() => {
    x.set(0)
    onChange(50)
  }, [usableWidth, onChange, x])

  const handleDrag = (): void => {
    const scrubberRect = vtoScrubberRef.current?.getBoundingClientRect()
    const sliderRect = sliderContainerRef.current?.getBoundingClientRect()
    if (scrubberRect != null && sliderRect != null) {
      const boundingWidth = Math.ceil(scrubberRect.width - sliderRect.width)
      const relativeX = Math.max(0, Math.floor(sliderRect.x - scrubberRect.x))
      const percent = 100 - parseInt(((relativeX / boundingWidth) * 100).toString())

      if (onChange !== undefined) {
        onChange(percent)
      }
    }
  }

  const cancelEvent = useCallback((event: any) => {
    if (event.cancelable) event.preventDefault()
  }, [])

  const preventScrolling = useCallback((event: any) => {
    document.addEventListener('touchmove', cancelEvent, { passive: false })
  }, [cancelEvent])

  const restoreScrolling = (event: any) => {
    document.removeEventListener('touchmove', cancelEvent)
  }

  const scale = usableWidth / WIDTH_BASIS
  const scaledRadius = RADIUS_BASIS * scale

  return (
    <FadeIn>
      <VtoScrubber x={x}
                   scale={scale}
                   slideFull={slideFull}
                   onDrag={handleDrag}
                   onDragStart={preventScrolling}
                   onDragEnd={restoreScrolling} />
      <SliderContainer ref={sliderContainerRef}>
        <Arrows key={`arrows_${usableWidth}`} scale={scale} />
        <Handle key={`handle_${usableWidth}`}
                radius={scaledRadius}
                x={x}
                scale={scale} />
      </SliderContainer>
    </FadeIn>
  )
}

export default SVGSlider
